/**
 * `useDashboardTotals` — fleet-wide inventory + refill rollup for the
 * Dashboard page. Mirrors `useBoxes`' shape: re-polls every
 * `pollIntervalSeconds`, refreshes on tab visibility change, exposes a
 * manual `refresh()` trigger. Scoped server-side via the auth token.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { configService } from "../services/configService";
import {
  fetchDashboardTotals,
  MttrApiError,
  type DashboardTotals,
} from "../services/mttrApi";

export interface UseDashboardTotalsResult {
  totals: DashboardTotals | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  lastFetchedAt: Date | null;
}

export function useDashboardTotals(): UseDashboardTotalsResult {
  const { token } = useAuth();
  const [totals, setTotals] = useState<DashboardTotals | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const intervalRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const data = await fetchDashboardTotals(token);
      setTotals(data);
      setLastFetchedAt(new Date());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e : new MttrApiError(String(e)));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();

    const cfg = configService.getConfig();
    const ms = Math.max(5, cfg.mttr.pollIntervalSeconds) * 1000;
    intervalRef.current = window.setInterval(() => {
      void refresh();
    }, ms);

    const onVisibility = (): void => {
      if (document.visibilityState === "visible") void refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (intervalRef.current) window.clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  return { totals, loading, error, refresh, lastFetchedAt };
}
